import { EnvironmentConfig } from '@/backend/extraTypes';
import { getErrorMessage } from '@juvare/common-web-ui/utils';
import { EditablePageData } from '@juvare/platform-sdk-react/backend/features/editable-pages/shared';
import { useEditablePages } from '@juvare/platform-sdk-react/ui/features/editable-pages';
import { usePublicSettingsLoader, useSettingsLoader } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useAppManifestLoader, usePlatformLoader } from '@juvare/platform-sdk-react/ui/hooks';
import { isUserLoggedIn } from '@juvare/platform-sdk-react/ui/stores';
import { combineLoadingStatuses } from '@juvare/platform-sdk-react/ui/utils';
import { Alert, Box } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { AnonymousApp } from './AnonymousApp';
import { AppContext } from './Context';
import { UserApp } from './UserApp';
import { useEnvironmentConfig } from './hooks';

export const App: React.FC = () => {
    // This code should exist in platform, not here, related to: https://jira.juvare.com/browse/PLAT-1245
    const pages = useEditablePages();
    const { data: environmentConfig, error: environmentConfigError } = useEnvironmentConfig();

    const status = combineLoadingStatuses([
        useAppManifestLoader({ autoRefreshSeconds: 60 }),
        usePlatformLoader({ appContext: AppContext }),
        usePublicSettingsLoader(),
        useSettingsLoader(),
    ]);
    const userLoggedIn = useSelector(isUserLoggedIn);
    if (!pages || !environmentConfig) {
        return <Box data-test='app-loading'>Loading...</Box>;
    } else if (environmentConfigError) {
        return <Alert security='error'>{getErrorMessage(environmentConfigError)}</Alert>;
    } else if (status === 'done') {
        return <AppContent userLoggedIn={userLoggedIn} config={environmentConfig} pages={pages} />;
    }

    return <>{status}</>;
};

export interface AppContentProps {
    config: EnvironmentConfig;
    pages: EditablePageData[];
    userLoggedIn: boolean;
}

const AppContent = React.memo(({ userLoggedIn, config, pages }: AppContentProps) => {
    return userLoggedIn ? <UserApp config={config} pages={pages} /> : <AnonymousApp config={config} pages={pages} />;
});
